import { Assignment, AssignmentState } from '../../api/app-types/assignment';
import { Lek } from '../../api/app-types/lek.app';
import { DB_PHASES, PHASE_TRANSITION_TYPES } from '../../api/app-types/phase.app';
import { normalizeToStartOfDay } from './date.func';

/**
 * Based on workflow given specifications, this methods resolves the state of an assignment based on existence of contacts and the current phase.
 * @param assignment The assignment to resolve the state for.
 * @returns The resolved state of the assignment.
 */
export function resolveAssignmentState(assignment?: Assignment): AssignmentState {
	// if there is no assignment or the assignment is in phase 0 and has no contacts yet then we do not have an assignment.
	if (!assignment || assignment.phaseId === DB_PHASES.P0) {
		return 'NO_ASSIGNMENT';
	}
	// if there is an assignment in phase 1 and it has contacts then we are in the contact phase (phase 1).
	if (assignment.phaseId === DB_PHASES.P1 && assignment.contacts.length > 0) {
		return 'P1';
	}
	// if there is an assignment in phase 2 then we are in phase 2.
	if (assignment.phaseId === DB_PHASES.P2) {
		return 'P2';
	}
	// if there is an assignment in phase 3 then we are in phase 3.
	if (assignment.phaseId === DB_PHASES.P3) {
		return 'P3';
	}
	// if there is an assignment in phase 4 then we are in phase 4.
	if (assignment.phaseId === DB_PHASES.P4) {
		return 'P4';
	}
	// default to no assignment.
	return 'NO_ASSIGNMENT';
}

export function determinePhaseTransitionType(assignment?: Assignment): PHASE_TRANSITION_TYPES {
	if (!assignment) return PHASE_TRANSITION_TYPES.UNKNOWN;

	if (!assignment.phaseChangeId) {
		let phaseChange = assignment.phase.id * 10 + 5;
		if (phaseChange === 15) phaseChange = 25; // Special case for P0 to P4 transition because for real it's P1 to P4
		return phaseChange;
	}

	return assignment.phase.id * 10 + assignment.phaseChangeId;
}

export function isValidDate(date: Date | null | undefined): date is Date {
	return !!date && !Number.isNaN(date.getTime());
}

/**
 * Caps future dates at today to avoid creating an invalid date range
 * where the calculated minimum date is after the maximum selectable date.
 */
export function capFutureDateToToday(date: Date, today = new Date()): Date {
	const day = normalizeToStartOfDay(date);
	const todayDay = normalizeToStartOfDay(today);

	return day > todayDay ? todayDay : day;
}

export function latestContactDate(assignment?: Assignment) {
	if (!assignment || assignment.contacts.length === 0) {
		return;
	}

	const contactDates = assignment.contacts
		.map((contact) => new Date(contact.contactedAt))
		.filter(isValidDate)
		.map((date) => capFutureDateToToday(date));

	if (contactDates.length === 0) {
		return;
	}

	return new Date(Math.max(...contactDates.map((date) => date.getTime())));
}

export function oldestContactDate(assignment?: Assignment) {
	if (!assignment || assignment.contacts.length === 0) {
		return;
	}
	const contactDates = assignment.contacts.map((c) => new Date(c.contactedAt));
	return new Date(Math.min(...contactDates.map((d) => d.getTime())));
}

export function latestLekEndDate(leks?: Lek[]) {
	if (!leks || leks.length === 0) {
		return;
	}

	const lekEndDates = leks
		.map((lek) => {
			if (!lek.endDate) {
				return null;
			}

			return lek.endDate instanceof Date ? lek.endDate : new Date(lek.endDate);
		})
		.filter((d): d is Date => !!d);

	if (lekEndDates.length === 0) {
		return;
	}

	return new Date(Math.max(...lekEndDates.map((d) => d.getTime())));
}
